import React from 'react';
import cx from 'classnames';
import { useMutation } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Card from '@material-ui/core/Card';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { ReactComponent as EditIcon } from 'assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import withConfirm from 'hooks/withConfirm';
import { TOPIC_TYPES } from 'constant/essay';
import { GET_ESSAY_TOPICS, DELETE_TOPIC } from 'graphql/essays';
import sum from 'lodash/sum';
import useSnack from 'hooks/useSnack';

const useStyles = makeStyles((theme) => ({
  chip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.main,
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 700,
    height: 40,
  },
  totalChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
    border: 'none',
    width: 60,
  },
  scoreLabel: {
    textTransform: 'uppercase',
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),

  },
  scoreChip: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    border: 'none',
    width: 60,
  },
}));

const Topics = ({ confirm, topics, student, onEdit, readOnly }) => {
  const classes = useStyles();
  const [deleteTopic] = useMutation(DELETE_TOPIC);
  const snack = useSnack();

  const onDelete = (row) => {
    confirm(
      () => {
        return deleteTopic({
          variables: { id: row.id },
          update: (proxy) => {
            const { essay_topics } = proxy.readQuery({
              query: GET_ESSAY_TOPICS,
              variables: { userId: student.id },
            });
            proxy.writeQuery({
              query: GET_ESSAY_TOPICS,
              variables: { userId: student.id },
              data: {
                essay_topics: essay_topics.filter((t) => t.id !== row.id),
              },
            });
          },
        }).then(
          () => snack.success('Topic deleted'),
          (e) => snack.error(e.message),
        );
      },
      {
        title: 'Remove Topic',
        description: (
          <>
            Do you want to remove the topic?
            <br /> <strong>IMPORTANT!</strong> Associated prompts with this
            topic will be deleted as well.
          </>
        ),
        confirmationText: 'Remove topic',
        confirmButtonProps: {
          variant: 'contained',
          color: 'secondary',
        },
      },
    );
  };

  return (
    <div>
      {topics.map((t) => {
        return (
          <Box
            component={Card}
            padding={3}
            marginBottom={3}
            display="flex"
            flexDirection="column"
          >
            <Box display="flex" alignItems="center" marginBottom={3}>
              <Chip
                color="info"
                className={classes.chip}
                label={TOPIC_TYPES.find((i) => i.value === t.type)?.label}
              />
              <Box marginLeft="auto">
                <IconButton disabled={readOnly} onClick={() => onEdit(t)}>
                  <EditIcon />
                </IconButton>
                <IconButton disabled={readOnly} edge="end" onClick={() => onDelete(t)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            <Typography paragraph variant="subtitle1">
              {t.idea}
            </Typography>
            <Typography variant="caption">
              {t.outcome}
            </Typography>
            <Box marginTop={4} marginBottom={4}>
              <Divider />
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography className={classes.scoreLabel}>Impact</Typography>
                <Chip
                  variant="caption"
                  className={cx(classes.chip, classes.scoreChip)}
                  label={t.impact}
                />
              </Box>
              <Box>
                <Typography className={classes.scoreLabel}>Like</Typography>
                <Chip
                  variant="caption"
                  className={cx(classes.chip, classes.scoreChip)}
                  label={t.like}
                />
              </Box>
              <Box>
                <Typography className={classes.scoreLabel}>Ease</Typography>
                <Chip
                  variant="caption"
                  className={cx(classes.chip, classes.scoreChip)}
                  label={t.ease}
                />
              </Box>
              <Box>
                <Typography className={classes.scoreLabel}>Total</Typography>
                <Chip
                  variant="caption"
                  className={cx(classes.chip, classes.totalChip)}
                  label={sum([t.impact, t.like, t.ease])}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </div>
  );
};

export default withConfirm(Topics);
